"use server";

import { cookies, headers } from "next/headers";
import { redirect } from "next/navigation";
import { revalidatePath } from "next/cache";
import { getDb } from "@/lib/db/client";
import { getCurrentMerchant, MERCHANT_COOKIE, merchantCookieOptions } from "@/lib/merchant-session";
import { addTile, createMerchant, disableSignedTaps, enableSignedTaps, rotateApiKey, setLogoUrl, updateProfile, validateProfile } from "@/lib/merchants";
import { checkOwnerKey } from "@/lib/owner";
import { clientIp, hit } from "@/lib/rate-limit";
import { merchantByApiKey } from "@/lib/sales";
import { MAX_LOGO_BYTES, removeLogo, saveLogo, sniffImage } from "@/lib/uploads";

export type FormState = {
  ok?: boolean;
  message?: string;
  errors?: Record<string, string>;
  /** a new key, shown once and never stored in the clear */
  apiKey?: string;
} | null;

const text = (fd: FormData, k: string) => (typeof fd.get(k) === "string" ? (fd.get(k) as string) : "");

// every action below finds the business from the cookie, never from the form,
// so one business can't edit another by changing a field in the request
async function requireMerchant() {
  const m = await getCurrentMerchant();
  if (!m) redirect("/merchant");
  return m;
}

// a key can't be guessed, the limit is there so nobody gets to try at speed
export async function signIn(_prev: FormState, fd: FormData): Promise<FormState> {
  const key = text(fd, "apiKey").trim();
  if (!hit(getDb(), `merchant-signin:ip:${clientIp(await headers())}`, 10, 10 * 60_000)) {
    return { errors: { apiKey: "Too many tries. Wait a few minutes and try again." } };
  }
  if (!merchantByApiKey(getDb(), key)) return { errors: { apiKey: "That key doesn't match a business." } };
  (await cookies()).set(MERCHANT_COOKIE, key, merchantCookieOptions);
  redirect("/merchant");
}

// Deliberately doesn't sign in. Setting the cookie here would make Next re-render
// the page as the dashboard and throw away the screen showing the new key, which
// is the only time the key is ever shown. "Continue" signs in with it afterwards.
const OWNER_KEY_ERRORS = {
  off: "Setting up businesses is turned off on this server.",
  wrong: "That isn't the owner key.",
  rate_limited: "Too many wrong tries. Wait 15 minutes and try again.",
};

export async function createBusiness(_prev: FormState, fd: FormData): Promise<FormState> {
  // checked before anything else, so someone without the key learns nothing from the form
  const owner = checkOwnerKey(getDb(), clientIp(await headers()), text(fd, "ownerKey"));
  if (!owner.ok) return { errors: { ownerKey: OWNER_KEY_ERRORS[owner.error] } };
  const name = text(fd, "name").trim();
  if (!name) return { errors: { name: "Enter the business name." } };
  if (name.length > 80) return { errors: { name: "Keep the name under 80 characters." } };
  const { apiKey } = createMerchant(getDb(), { name, nameAr: text(fd, "nameAr").slice(0, 80) });
  return { ok: true, apiKey };
}

export async function signOutMerchant() {
  (await cookies()).delete(MERCHANT_COOKIE);
  redirect("/merchant");
}

export async function saveProfile(_prev: FormState, fd: FormData): Promise<FormState> {
  const merchant = await requireMerchant();
  const db = getDb();

  const parsed = validateProfile({
    name: text(fd, "name"),
    nameAr: text(fd, "nameAr"),
    vatNumber: text(fd, "vatNumber"),
    brandColor: text(fd, "brandColor"),
  });
  const errors: Record<string, string> = parsed.ok ? {} : { ...parsed.errors };

  // check the logo before changing anything, so a bad file doesn't half save the form
  const file = fd.get("logo");
  let logo: { bytes: Uint8Array; kind: NonNullable<ReturnType<typeof sniffImage>> } | null = null;
  if (file instanceof File && file.size > 0) {
    if (file.size > MAX_LOGO_BYTES) errors.logo = "That image is over 512 KB. Use a smaller one.";
    else {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const kind = sniffImage(bytes);
      if (kind) logo = { bytes, kind };
      else errors.logo = "Use a PNG, JPEG or WebP image.";
    }
  }
  if (Object.keys(errors).length || !parsed.ok) return { errors };

  updateProfile(db, merchant.id, parsed.value);
  if (logo) {
    const saved = saveLogo(logo.bytes, logo.kind, merchant.id);
    if (merchant.logoUrl !== saved.url) removeLogo(merchant.logoUrl);
    setLogoUrl(db, merchant.id, saved.url);
  } else if (text(fd, "removeLogo") === "on") {
    removeLogo(merchant.logoUrl);
    setLogoUrl(db, merchant.id, null);
  }

  revalidatePath("/merchant");
  return { ok: true, message: "Saved. New receipts use these details straight away." };
}

export async function addTileAction(_prev: FormState, fd: FormData): Promise<FormState> {
  const merchant = await requireMerchant();
  const res = addTile(getDb(), merchant.id, text(fd, "label"));
  if (!res.ok) return { errors: { label: res.error } };
  revalidatePath("/merchant");
  return { ok: true, message: `Added ${res.tile.label}.` };
}

export async function rotateKey(): Promise<FormState> {
  const merchant = await requireMerchant();
  const apiKey = rotateApiKey(getDb(), merchant.id);
  (await cookies()).set(MERCHANT_COOKIE, apiKey, merchantCookieOptions);
  return { ok: true, apiKey };
}

// turn=on also replaces an existing key, which is how a re-programmed sticker starts over
export async function signedTapsAction(_prev: FormState, fd: FormData): Promise<FormState> {
  const merchant = await requireMerchant();
  const tileId = text(fd, "tileId");
  const db = getDb();
  if (text(fd, "turn") === "off") {
    if (!disableSignedTaps(db, merchant.id, tileId)) return { errors: { tile: "That tile isn't on this account." } };
    revalidatePath("/merchant");
    return { ok: true, message: "Signed taps are off. The plain link claims again." };
  }
  if (!enableSignedTaps(db, merchant.id, tileId)) return { errors: { tile: "That tile isn't on this account." } };
  revalidatePath("/merchant");
  return { ok: true, message: "New key made. Program it into the sticker before the next sale." };
}
